/**
 * UI-8f Task 5 — CreateIncidentDrawer.
 *
 * Slide-in drawer for creating a new incident. Composed of:
 *
 *   - Title       InputText (required, 1-200)
 *   - Severity    SelectButton (4 options LOW/MEDIUM/HIGH/CRITICAL, default MEDIUM)
 *   - Description InputTextarea (required, 1-5000)
 *
 * Save calls `useIncidents().create({ title, severity, description })` which
 * POSTs `/platform/incidents` and refetches the list. The drawer resets
 * its form on close so the next open starts clean (matches NewFlagDrawer
 * from UI-8e).
 *
 * Why not <ZodForm>? UI-9a's form framework is the standard for typed
 * forms, but this drawer is intentionally tiny (3 fields) — direct
 * useState matches the sibling NewFlagDrawer / CreateIncidentDrawer
 * pattern on the platform surface. Future refactor welcome.
 */
import { useState, type JSX } from 'react'
import { Siren, X } from 'lucide-react'

import { useIncidents } from '../../hooks/useIncidents.js'

export interface CreateIncidentDrawerProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

const SEVERITY_OPTIONS = [
  { label: 'LOW', value: 'LOW', color: 'bg-gray-100 text-gray-500' },
  { label: 'MEDIUM', value: 'MEDIUM', color: 'bg-blue-50 text-blue-700' },
  { label: 'HIGH', value: 'HIGH', color: 'bg-amber-50 text-amber-700' },
  { label: 'CRITICAL', value: 'CRITICAL', color: 'bg-red-50 text-red-700' },
]

export function CreateIncidentDrawer({ open, onClose, onSaved }: CreateIncidentDrawerProps): JSX.Element {
  const { create } = useIncidents()

  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('MEDIUM')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const valid = title.trim().length > 0 && description.trim().length > 0

  function resetForm(): void {
    setTitle('')
    setSeverity('MEDIUM')
    setDescription('')
  }

  function handleClose(): void { resetForm(); onClose() }

  async function handleSave(): Promise<void> {
    if (!valid) return
    setSaving(true)
    try {
      await create({ title, severity, description })
      resetForm()
      onSaved?.()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  if (!open) return <></>

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={handleClose} />
      <div className="fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl z-[9999] flex flex-col transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <Siren className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">New Incident</h2>
              <p className="text-xs text-gray-400">Log a new operational incident.</p>
            </div>
          </div>
          <button onClick={handleClose} className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Incident Details</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-incident-title" className="text-sm font-semibold text-gray-700">Title <span className="text-red-500">*</span></label>
                <input
                  id="new-incident-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Database slowness"
                  maxLength={200}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Severity</label>
                <div className="flex gap-1">
                  {SEVERITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSeverity(opt.value)}
                      className={`flex-1 px-3 py-2 text-xs font-semibold rounded-sm transition-all border ${
                        severity === opt.value
                          ? `${opt.color} border-current`
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-incident-description" className="text-sm font-semibold text-gray-700">Description <span className="text-red-500">*</span></label>
                <textarea
                  id="new-incident-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="What happened? Symptoms, scope, first signal."
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-white shrink-0">
          <button onClick={handleClose} className="text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={!valid || saving}
            className="text-sm font-semibold px-5 py-2.5 rounded-sm bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white transition-all flex items-center gap-2"
          >
            {saving && <i className="pi pi-spin pi-spinner text-xs" />}
            Create Incident
          </button>
        </div>
      </div>
    </>
  )
}

export default CreateIncidentDrawer
